import {useQueryClient, type Query} from '@github-ui/react-query'
import {useCallback} from 'react'

import {milestoneEditQueryKey} from './milestone-edit-route'
import {isRepositoryMilestoneIndexQuery} from './milestone-index-route'
import {milestoneHeaderQueryKey} from './milestone-show-route'

/**
 * Refreshes every cached index variant for this repository and, by default, one Show header.
 * Omitting the milestone number performs index-only synchronization.
 * Cancel before invalidating or removing so pre-write reads cannot restore stale data.
 *
 * The affected milestone's Edit route query, if cached, is always removed rather than invalidated:
 * that route is a Blocking query, so removal forces the next Edit visit — including browser Back
 * to a previously warm cache — to block on a fresh fetch instead of initializing `MilestoneForm`'s
 * one-time `useState` from a stale payload that a background revalidation would not repair.
 * Index close/delete also evict inactive variants so changing tabs cannot render their old data.
 */
export function useMilestoneCacheSync(owner: string, repo: string) {
  const queryClient = useQueryClient()
  const pathname = `/${owner}/${repo}/milestones`

  return useCallback(
    async (
      milestoneNumber?: number,
      {
        header = 'invalidate',
        evictInactiveIndex = false,
      }: {header?: 'invalidate' | 'remove' | 'skip'; evictInactiveIndex?: boolean} = {},
    ) => {
      const indexPredicate = (query: Query) => isRepositoryMilestoneIndexQuery(query, pathname)
      const headerQueryKey =
        milestoneNumber === undefined || header === 'skip'
          ? undefined
          : milestoneHeaderQueryKey(owner, repo, milestoneNumber)
      const editQueryKey =
        milestoneNumber === undefined ? undefined : milestoneEditQueryKey(owner, repo, milestoneNumber)

      await Promise.all([
        queryClient.cancelQueries({predicate: indexPredicate}),
        headerQueryKey ? queryClient.cancelQueries({queryKey: headerQueryKey, exact: true}) : undefined,
        editQueryKey ? queryClient.cancelQueries({queryKey: editQueryKey, exact: true}) : undefined,
      ])
      if (evictInactiveIndex) {
        queryClient.removeQueries({predicate: indexPredicate, type: 'inactive'})
      }
      if (headerQueryKey && header === 'remove') {
        queryClient.removeQueries({queryKey: headerQueryKey, exact: true})
      }
      if (editQueryKey) {
        queryClient.removeQueries({queryKey: editQueryKey, exact: true})
      }
      await Promise.all([
        queryClient.invalidateQueries({predicate: indexPredicate}),
        headerQueryKey && header === 'invalidate'
          ? queryClient.invalidateQueries({queryKey: headerQueryKey, exact: true})
          : undefined,
      ])
    },
    [owner, pathname, queryClient, repo],
  )
}
